<template>
  <div ref="root" class="msel" :class="{ 'is-open': open }" @keydown.esc="close">
    <label v-if="label" class="msel__label">{{ label }}</label>
    <button
      type="button"
      class="msel__control"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="msel__text" :class="{ 'is-none': none }">{{ summary }}</span>
      <i class="msel__chevron fas fa-chevron-down"></i>
    </button>

    <div v-if="open" class="msel__backdrop" @click="close"></div>
    <div v-if="open" ref="panel" class="msel__panel" :class="{ 'is-right': alignRight }" role="listbox" aria-multiselectable="true">
      <div class="msel__bar">
        <button type="button" class="msel__link" :disabled="all" @click="selectAll">Выбрать все</button>
        <button type="button" class="msel__link" :disabled="none" @click="clear">Снять все</button>
        <button type="button" class="msel__done" @click="close">Готово</button>
      </div>
      <label v-if="searchable" class="msel__search">
        <i class="fas fa-search"></i>
        <input v-model="query" type="search" :placeholder="searchPlaceholder" autocomplete="off">
      </label>
      <div class="msel__list">
        <label v-for="o in filteredOptions" :key="o.value" class="msel__opt" :class="{ 'is-on': isOn(o.value) }">
          <input type="checkbox" :checked="isOn(o.value)" @change="toggleOne(o.value)">
          <span>{{ o.label }}</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
// Выбор нескольких значений из списка (чекбоксы в выпадающей панели; на телефоне панель — шторка снизу).
// v-model — массив выбранных значений. «Всё выбрано» и «ничего не выбрано» показываются своими подписями.
export default {
  name: 'AppMultiSelect',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: Array, default: () => [] },
    // [{ value, label }]
    options: { type: Array, default: () => [] },
    label: { type: String, default: '' },
    // подпись, когда выбрано всё («Все пользователи»)
    allLabel: { type: String, default: 'Все' },
    noneLabel: { type: String, default: 'Ничего не выбрано' },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Поиск…' },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return { open: false, alignRight: false, query: '' }
  },
  computed: {
    selected() {
      return new Set(this.value)
    },
    count() {
      return this.options.filter((o) => this.selected.has(o.value)).length
    },
    all() {
      return this.options.length > 0 && this.count === this.options.length
    },
    none() {
      return this.count === 0
    },
    summary() {
      if (this.all || !this.options.length) return this.allLabel
      if (this.none) return this.noneLabel
      const picked = this.options.filter((o) => this.selected.has(o.value))
      if (picked.length <= 2) return picked.map((o) => o.label).join(', ')
      return 'Выбрано ' + picked.length + ' из ' + this.options.length
    },
    filteredOptions() {
      const words = this.query.trim().toLowerCase().split(/\s+/).filter(Boolean)
      if (!words.length) return this.options
      return this.options.filter((option) => words.every((word) => String(option.label).toLowerCase().includes(word)))
    }
  },
  watch: {
    open(v) {
      if (v) {
        document.addEventListener('click', this.onOutside, true)
        this.$nextTick(this.place)
      } else {
        document.removeEventListener('click', this.onOutside, true)
      }
    }
  },
  methods: {
    isOn(v) {
      return this.selected.has(v)
    },
    toggle() {
      this.open = !this.open
      if (this.open) this.query = ''
    },
    close() {
      this.open = false
    },
    onOutside(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) this.close()
    },
    // панель не выходит за правый край экрана
    place() {
      this.alignRight = false
      const panel = this.$refs.panel
      if (panel && panel.getBoundingClientRect().right > window.innerWidth - 8) this.alignRight = true
    },
    emit(values) {
      // порядок как в списке
      this.$emit('input', this.options.filter((o) => values.has(o.value)).map((o) => o.value))
    },
    toggleOne(v) {
      const next = new Set(this.selected)
      if (next.has(v)) next.delete(v)
      else next.add(v)
      this.emit(next)
    },
    selectAll() {
      this.$emit('input', this.options.map((o) => o.value))
    },
    clear() {
      this.$emit('input', [])
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onOutside, true)
  }
}
</script>

<style scoped>
.msel { position: relative; }
.msel__label {
  display: block;
  margin-bottom: var(--space-2);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}
.msel__control {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--space-4) var(--space-20) var(--space-4) var(--space-8);
  font: inherit;
  font-size: var(--text-base);
  text-align: left;
  color: var(--text-primary);
  background: var(--bg-input);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  border-radius: var(--radius-input);
  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease-base), box-shadow var(--duration-normal) var(--ease-out);
}
.msel__control:hover:not(:disabled) { border-color: var(--border-strong); }
.msel__control:focus-visible, .msel.is-open .msel__control { outline: none; border-color: var(--border-focus); box-shadow: var(--focus-ring); }
.msel__control:disabled { cursor: not-allowed; opacity: 0.6; }
.msel__text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.msel__text.is-none { color: var(--text-tertiary); }
.msel__chevron {
  position: absolute;
  right: var(--space-8);
  top: 50%;
  transform: translateY(-50%);
  font-size: var(--text-xs);
  color: var(--text-tertiary);
  pointer-events: none;
  transition: transform var(--duration-fast) var(--ease-base);
}
.msel.is-open .msel__chevron { transform: translateY(-50%) rotate(180deg); }

.msel__backdrop { display: none; }
.msel__panel {
  position: absolute;
  top: calc(100% + var(--space-2));
  left: 0;
  z-index: var(--z-dropdown);
  display: flex;
  flex-direction: column;
  min-width: 100%;
  width: max-content;
  max-width: min(26rem, calc(100vw - 16px));
  max-height: min(24rem, 60vh);
  background: var(--bg-surface);
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-xl);
}
.msel__panel.is-right { left: auto; right: 0; }
.msel__bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: var(--space-4) var(--space-8);
  border-bottom: var(--border-width-default) solid var(--border-default);
}
.msel__link {
  padding: var(--space-2) 0;
  font: inherit;
  font-size: var(--text-sm);
  color: var(--text-link);
  background: none;
  border: 0;
  cursor: pointer;
}
.msel__link:hover:not(:disabled) { text-decoration: underline; }
.msel__link:disabled { color: var(--text-tertiary); cursor: default; }
.msel__done { display: none; margin-left: auto; }
.msel__list { overflow-y: auto; padding: var(--space-2) 0; }
.msel__search { display: flex; align-items: center; gap: var(--space-5); padding: var(--space-5) var(--space-8); border-bottom: var(--border-width-default) solid var(--border-default); color: var(--text-tertiary); }
.msel__search input { min-width: 0; flex: 1; border: 0; outline: 0; color: var(--text-primary); background: transparent; font: inherit; }
.msel__opt {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  padding: var(--space-4) var(--space-8);
  font-size: var(--text-sm);
  color: var(--text-primary);
  cursor: pointer;
}
.msel__opt:hover { background: var(--bg-subtle); }
.msel__opt.is-on { background: var(--primary-soft-2); }
.msel__opt input { flex-shrink: 0; width: 18px; height: 18px; margin: 0; accent-color: var(--primary); }

/* телефон: панель — шторка снизу на подложке, строки и кнопки по 44 px */
@media (max-width: 640px) {
  .msel__backdrop { display: block; position: fixed; inset: 0; z-index: var(--z-dropdown); background: rgba(15, 23, 42, 0.4); }
  .msel__panel,
  .msel__panel.is-right {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: calc(var(--z-dropdown) + 1);
    width: auto;
    max-width: none;
    max-height: 75dvh;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .msel__bar { padding: var(--space-4) var(--space-10); }
  .msel__link { min-height: 44px; font-size: var(--text-base); }
  .msel__done {
    display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: 0 var(--space-12);
    font: inherit; font-weight: var(--font-weight-semibold); color: #fff; background: var(--primary); border: 0; border-radius: var(--radius-md); cursor: pointer;
  }
  .msel__opt { min-height: 48px; padding: var(--space-4) var(--space-10); font-size: var(--text-base); }
  .msel__opt input { width: 22px; height: 22px; }
}
</style>
